import type {
    CompositionStructureCheckpointSnapshotV1,
    ContributionCheckpointDraftV1,
} from "./types";

export interface CompositionStructureCheckpointInput {
    compositionId: string;
    display?: {
        width?: number;
        height?: number;
        backgroundColor?: string;
    };
    durationSec?: number;
    isEmpty?: boolean;
    orderedLayerIds?: string[];
    layers?: Record<string, { id?: string; clipIds?: string[] }>;
    clips?: Record<string, {
        id?: string;
        type?: string;
        startTime?: number;
        duration?: number;
        trimmedDuration?: number;
    }>;
}

const finiteNonNegative = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const finitePositive = (value: unknown, fallback: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export function canonicalJson(value: unknown): string {
    const normalize = (input: unknown): unknown => {
        if (Array.isArray(input)) return input.map(normalize);
        if (!input || typeof input !== "object") return input;
        const output: Record<string, unknown> = {};
        for (const key of Object.keys(input as Record<string, unknown>).sort()) {
            const next = (input as Record<string, unknown>)[key];
            if (next !== undefined) output[key] = normalize(next);
        }
        return output;
    };
    return JSON.stringify(normalize(value));
}

const bytesToHex = (bytes: Uint8Array): string =>
    Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

export async function sha256CanonicalJson(value: unknown): Promise<string | undefined> {
    try {
        if (typeof crypto === "undefined" || !crypto.subtle?.digest) return undefined;
        const bytes = new TextEncoder().encode(canonicalJson(value));
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        return bytesToHex(new Uint8Array(digest));
    } catch {
        return undefined;
    }
}

export async function buildCompositionStructureCheckpoint(
    input: CompositionStructureCheckpointInput,
): Promise<ContributionCheckpointDraftV1> {
    const orderedLayerIds = Array.from(new Set(
        (input.orderedLayerIds ?? []).map((id) => String(id ?? "").trim()).filter(Boolean),
    ));
    const snapshot: CompositionStructureCheckpointSnapshotV1 = {
        schemaVersion: 1,
        compositionId: String(input.compositionId ?? ""),
        display: {
            width: finitePositive(input.display?.width, 1920),
            height: finitePositive(input.display?.height, 1080),
            backgroundColor: String(input.display?.backgroundColor ?? "#000000"),
        },
        durationSec: finiteNonNegative(input.durationSec),
        isEmpty: input.isEmpty !== false,
        layers: orderedLayerIds.map((layerId) => {
            const layer = input.layers?.[layerId];
            const clipIds = Array.from(new Set(
                (layer?.clipIds ?? []).map((id) => String(id ?? "").trim()).filter(Boolean),
            ));
            return {
                layerId,
                clips: clipIds.map((clipId) => {
                    const clip = input.clips?.[clipId];
                    return {
                        clipId,
                        type: String(clip?.type ?? "unknown"),
                        startSec: finiteNonNegative(clip?.startTime),
                        durationSec: finiteNonNegative(clip?.duration),
                        trimmedDurationSec: finiteNonNegative(clip?.trimmedDuration),
                    };
                }),
            };
        }),
    };
    const hash = await sha256CanonicalJson(snapshot);
    return {
        format: "composition_structure_v1",
        completeness: "structure_only",
        ...(hash ? { stateHash: { algorithm: "SHA-256", value: hash } as const } : {}),
        snapshot,
    };
}
